import get from 'lodash/get';
import includes from 'lodash/includes';
import isEqual from 'lodash/isEqual';
import isNumber from 'lodash/isNumber';
import isNull from 'lodash/isNull';
import last from 'lodash/last';
import keys from 'lodash/keys';
import round from 'lodash/round';
import split from 'lodash/split';
import toString from 'lodash/toString';
import moment from 'moment';
import { formatThousands } from '../numbers/numbers';
import { CURRENCY_SYMBOLS } from './../const/currencies.const';
import { DISTANCE_UNIT, KILOMETERS_PER_MILE } from './../const/distance.const';
import { LOG_TYPE } from './../const/logger.const';
import { logger } from './../logger/logger.class';
export function getUtcString(value = '') {
    const numericValue = isNumber(value) ? value : parseInt(value, 10);
    const isTimestamp = isNumber(value) || isEqual(toString(numericValue), value);
    const date = moment.parseZone(value);
    const dateTime = isTimestamp
        ? new Date(numericValue * 1000)
        : date.isValid() ? date.toDate() : new Date(value);
    return dateTime.toUTCString();
}
/**
 * Transforms number of kilometers into miles
 * @param value number, string - distance in kilometers
 * @param precision numnber - represeniting desired outcome precission
 * @param unit string
 */
export function getMiles(value, precision = 3, unit = DISTANCE_UNIT.miles) {
    const numericValue = isNumber(value) ? value : parseInt(value, 10);
    if (!numericValue || isNaN(numericValue)) {
        return 0;
    }
    const miles = numericValue / KILOMETERS_PER_MILE;
    const distance = formatThousands(round(miles, precision));
    return unit ? `${distance} ${unit}` : distance;
}
/**
 * Formats number as kilometers
 * @param value number, string - distance in meters
 * @param precision numnber - represeniting desired outcome precission
 * @param unit string
 */
export function formatKilometers(value, precision = 3, unit = DISTANCE_UNIT.kilometers) {
    const numericValue = isNumber(value) ? value : parseInt(value, 10);
    if (!numericValue || isNaN(numericValue)) {
        return 0;
    }
    const distance = formatThousands(round(numericValue, precision));
    return unit ? `${distance} ${unit}` : distance;
}
/**
 * Formats number as currency assigning it's respective symbol
 * @param value `number | string` amount to format
 * @param symbol `string` three character currency code
 * @param precision `number` number of decimal places the value is rounded to
 */
export function formatCurrency(value, currencyCode = CURRENCY_SYMBOLS.EUR.code, precision = 2) {
    const numericValue = isNumber(value) ? value : parseInt(value, 10);
    const currencyCodes = keys(CURRENCY_SYMBOLS);
    if (!includes(currencyCodes, currencyCode)) {
        logger.message('Incorrect currency code', LOG_TYPE.warning);
        return value.toString() || '0';
    }
    const currency = get(CURRENCY_SYMBOLS, currencyCode);
    if (isNaN(numericValue)) {
        logger.message('Invalid amount', LOG_TYPE.error);
        return '';
    }
    const amount = isNull(precision)
        ? formatThousands(numericValue, ',')
        : formatThousands(numericValue.toFixed(precision), ',');
    return currency.isSymbolToTheLeft ? `${currency.symbol}${amount}` : `${amount}${currency.symbol}`;
}
/**
 * Formats integer into currency assigning it's respective symbol
 * @param value number - amount to format as Integer (4200 will result in 42.00)
 * @param symbol string
 */
export function formatPreciseCurrency(value, currencyCode = CURRENCY_SYMBOLS.EUR.code) {
    const numericValue = ((isNumber(value) ? value : parseInt(value, 10)) / 100).toFixed(2);
    const currencyCodes = keys(CURRENCY_SYMBOLS);
    if (!includes(currencyCodes, currencyCode)) {
        logger.message('Incorrect currency currency code', LOG_TYPE.warning);
        return `${value}` || '0';
    }
    const currency = get(CURRENCY_SYMBOLS, currencyCode);
    if (!numericValue) {
        logger.message('Invalid amount', LOG_TYPE.error);
        return currency.isSymbolToTheLeft ? `${currency.symbol}0.00` : `0.00${currency.symbol}`;
    }
    const amount = currency.delimiter ? numericValue.replace('.', currency.delimiter) : numericValue;
    return currency.isSymbolToTheLeft ? `${currency.symbol}${amount}` : `${amount}${currency.symbol}`;
}
/**
 * Get's user uuid from identity provider
 * @param value IDP token
 * @param delimiter character splitting IDP name and the user token
 * @returns string
 */
export function getIdpUuid(value = '', delimiter = '|') {
    return isNumber(value) ? toString(value) : last(split(value, delimiter)) || '';
}
